// The app-wide music update check (issue #3517).
//
// One module-level, re-runnable, coalescing entry point - refreshMusicCache() - callable from
// outside React. Its inputs live in a small store that the React providers push into via effects,
// so app start, a return to the tab, the network coming back and (per #3265) a socket ping can all
// run the same walk without a component being mounted.
//
// The name refreshMusicCache and the coalescing semantics are the contract with #3265:
// a call during a walk causes exactly one more walk - never zero, never two.
import { AppHashes, CustomMusic, OrgId } from '../../../common/src/model.ts';
import { computeAggregateHash } from '../../../common/src/model_util.ts';
import { customMusicDir, hymnalsDir, musicManifestFilename } from '../../../common/src/paths.ts';
import * as server_api from '../../../common/src/server_api.ts';
import {
  applyClientCacheRefreshes,
  ClientCacheName,
  DownloadFunction,
  queueManifestsMissingFromStorage,
} from '../data/client_cache_storage.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { HymnalWithHymns } from '../data/hymnals.ts';
import { DownloadQueueAction } from '../data/idb.ts';
import { getManifestEntryFromHymn, refreshOrgHymnal } from '../data/use_hymnals.tsx';
import { noteServerBundleHash } from './app_update.ts';
import { getUrlPrefixForSong } from './path.ts';
import { DownloadsCompleteFunction } from './use_downloads.tsx';

// Ambient triggers (a return to the tab, a page mount) are cheap to fire and can arrive in
// bursts, so they are skipped shortly after a completed walk. Explicit triggers never are.
const ambientThrottleMs = 30 * 1000;

export type MusicCacheRefreshTrigger = 'start' | 'online' | 'visible' | 'mount' | 'explicit';

const ambientTriggers: MusicCacheRefreshTrigger[] = ['visible', 'mount'];

interface MusicCacheStore {
  orgId: OrgId | undefined;
  hymnals: HymnalWithHymns[];
  customMusic: CustomMusic;
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
  isOnline: boolean;
  addDownload: DownloadFunction | undefined;
  downloadsComplete: DownloadsCompleteFunction | undefined;
}

const store: MusicCacheStore = {
  orgId: undefined,
  hymnals: [],
  customMusic: {},
  customMusicLoadedOrgIds: new Set(),
  isOnline: true,
  addDownload: undefined,
  downloadsComplete: undefined,
};

export interface MusicCacheWalkInputs {
  orgId: OrgId | undefined;
  hymnals: HymnalWithHymns[];
  customMusic: CustomMusic;
  customMusicLoadedOrgIds: ReadonlySet<OrgId>;
  addDownload: DownloadFunction;
  downloadsComplete: DownloadsCompleteFunction;
}

export type MusicCacheWalk = (inputs: MusicCacheWalkInputs) => Promise<void>;

// Injectable for tests (issue #3517) - production always uses the real clock and walk.
let clock: () => number = () => Date.now();
let walk: MusicCacheWalk = walkMusicCache;

export function setMusicCacheRefreshClock(newClock: () => number) {
  clock = newClock;
}

export function setMusicCacheWalk(newWalk: MusicCacheWalk) {
  walk = newWalk;
}

export function resetMusicCacheRefreshInjections() {
  clock = () => Date.now();
  walk = walkMusicCache;
}

/********************************** the store **********************************/

export function setMusicCacheOrgId(orgId: OrgId | undefined) {
  if (store.orgId === orgId) {
    return;
  }
  store.orgId = orgId;
  // a different church means a different org hymnal and different app hashes, so check now rather
  // than waiting for the next ambient trigger (issue #3517)
  void refreshMusicCache({ trigger: 'explicit' });
}

export function setMusicCacheHymnals(hymnals: HymnalWithHymns[]) {
  const wasEmpty = store.hymnals.length === 0;
  store.hymnals = hymnals;
  if (wasEmpty && hymnals.length > 0) {
    // the org hymnal pre-download cannot run without hymnals, so check again now that they are
    // here rather than waiting for the next ambient trigger (issue #3517 item A1)
    void refreshMusicCache({ trigger: 'explicit' });
  }
}

export function setMusicCacheCustomMusic(customMusic: CustomMusic) {
  const wasEmpty = Object.keys(store.customMusic).length === 0;
  store.customMusic = customMusic;
  if (wasEmpty && Object.keys(customMusic).length > 0) {
    void refreshMusicCache({ trigger: 'explicit' });
  }
}

// The org hymnal pre-download waits until the current church's custom music is known, so the
// moment it becomes known is a trigger. Only that transition fires: a church whose songs stay
// unknown (offline, or a failed manifest fetch) waits idle rather than retrying.
export function setMusicCacheCustomMusicLoadedOrgIds(customMusicLoadedOrgIds: ReadonlySet<OrgId>) {
  const { orgId } = store;
  const wasLoaded = orgId !== undefined && store.customMusicLoadedOrgIds.has(orgId);
  store.customMusicLoadedOrgIds = customMusicLoadedOrgIds;
  if (orgId !== undefined && !wasLoaded && customMusicLoadedOrgIds.has(orgId)) {
    void refreshMusicCache({ trigger: 'explicit' });
  }
}

export function setMusicCacheOnline(isOnline: boolean) {
  store.isOnline = isOnline;
}

export function setMusicCacheDownloadFunctions({
  addDownload,
  downloadsComplete,
}: {
  addDownload: DownloadFunction;
  downloadsComplete: DownloadsCompleteFunction;
}) {
  store.addDownload = addDownload;
  store.downloadsComplete = downloadsComplete;
}

// tests only - the store lives for the session in the app
export function resetMusicCacheStore() {
  store.orgId = undefined;
  store.hymnals = [];
  store.customMusic = {};
  store.customMusicLoadedOrgIds = new Set();
  store.isOnline = true;
  store.addDownload = undefined;
  store.downloadsComplete = undefined;
  inFlight = undefined;
  waiting = [];
  lastCompletedAt = undefined;
  triggersRegistered = false;
}

/********************************** the coalescer **********************************/

let inFlight: Promise<void> | undefined;
let waiting: Array<() => void> = [];
let lastCompletedAt: number | undefined;

// Runs the music update check. While a walk is in flight, callers are queued: exactly one more
// walk runs when the current one finishes, and every queued caller's promise resolves when that
// follow-up walk completes. Never throws.
export function refreshMusicCache({ trigger }: { trigger?: MusicCacheRefreshTrigger } = {}): Promise<void> {
  if (inFlight) {
    return new Promise<void>((resolve) => waiting.push(resolve));
  }
  if (isThrottled(trigger ?? 'explicit')) {
    return Promise.resolve();
  }
  const firstWalk = runWalk();
  inFlight = firstWalk;
  void driveWalkChain(firstWalk);
  return firstWalk;
}

function isThrottled(trigger: MusicCacheRefreshTrigger): boolean {
  if (!ambientTriggers.includes(trigger)) {
    return false;
  }
  return lastCompletedAt !== undefined && clock() - lastCompletedAt < ambientThrottleMs;
}

async function driveWalkChain(firstWalk: Promise<void>) {
  let current = firstWalk;
  let waitersForCurrent: Array<() => void> = [];
  for (;;) {
    try {
      await current;
    } catch (error: any) {
      // runWalk swallows walk errors; this only guards against a bug in the bookkeeping itself
      console.log(`Music cache refresh failed: ${error?.message ?? error}`);
    }
    inFlight = undefined;
    for (const resolve of waitersForCurrent) {
      resolve();
    }
    if (waiting.length === 0) {
      return;
    }
    waitersForCurrent = waiting;
    waiting = [];
    current = runWalk();
    inFlight = current;
  }
}

async function runWalk(): Promise<void> {
  const { orgId, hymnals, customMusic, customMusicLoadedOrgIds, isOnline, addDownload, downloadsComplete } = store;
  if (!isOnline || !addDownload || !downloadsComplete) {
    // offline, or the download queue is not mounted yet - no completion is recorded, so the next
    // ambient trigger is not throttled by a check that never ran
    return;
  }
  try {
    await walk({ orgId, hymnals, customMusic, customMusicLoadedOrgIds, addDownload, downloadsComplete });
  } catch (error: any) {
    // a broken walk must be visible, not swallowed (issue #3517) - most often a network error,
    // in which case the check simply waits for the network to come back
    console.log(`Music cache refresh failed: ${error?.message ?? error}`);
  }
  lastCompletedAt = clock();
}

/********************************** the triggers **********************************/

let triggersRegistered = false;

// Registered once from initializeApp: the check no longer depends on a page being mounted.
export function registerMusicCacheRefreshTriggers() {
  if (triggersRegistered) {
    return;
  }
  triggersRegistered = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    void refreshMusicCache({ trigger: 'visible' });
  });

  window.addEventListener('online', () => {
    // our listener can run before React's own online subscription updates the store
    setMusicCacheOnline(true);
    void refreshMusicCache({ trigger: 'online' });
  });

  // the org hymnal pre-download needs a controlling service worker (issue #3517 item A1)
  const serviceWorker = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
  if (serviceWorker) {
    serviceWorker.addEventListener('controllerchange', () => {
      void refreshMusicCache({ trigger: 'explicit' });
    });
    void serviceWorker.ready.then(() => refreshMusicCache({ trigger: 'explicit' })).catch(() => undefined);
  }

  void refreshMusicCache({ trigger: 'start' });
}

/********************************** the walk **********************************/

async function walkMusicCache({
  orgId,
  hymnals,
  customMusic,
  customMusicLoadedOrgIds,
  addDownload,
  downloadsComplete,
}: MusicCacheWalkInputs) {
  const serverAppHashes: AppHashes = await server_api.getAppHashes({ orgId });
  // The same response that carries the music hashes says which bundle the server is serving, so
  // the walk is also how a client notices a new build (issue #3265). Not awaited: a service-worker
  // check takes a second or two and the music refresh below must not queue behind it.
  void noteServerBundleHash(serverAppHashes.bundleHash);
  await refreshCache({
    serverHash: serverAppHashes.customMusicHash,
    cacheName: ClientCacheName.CustomMusic,
    dirPath: `/${customMusicDir}`,
    localStorageKey: LocalStorageKey.CustomMusicHash,
    addDownload,
    downloadsComplete,
  });
  await refreshCache({
    serverHash: serverAppHashes.hymnalsHash,
    cacheName: ClientCacheName.Hymnals,
    dirPath: `/${hymnalsDir}`,
    localStorageKey: LocalStorageKey.HymnalsHash,
    addDownload,
    downloadsComplete,
  });
  // after the two walks above, which store the root manifests themselves
  await queueManifestsMissingFromStorage(addDownload);
  await refreshOrgHymnal({
    serverHash: serverAppHashes.orgHymnalHash,
    orgId,
    addDownload,
    hymnals,
    customMusic,
    customMusicLoadedOrgIds,
    downloadsComplete,
  });
  await downloadFavorites(addDownload);
  await applyClientCacheRefreshes(addDownload);
}

// TODO(hewitt): Move code down into service worker for background threading
async function refreshCache({
  serverHash,
  cacheName,
  dirPath,
  localStorageKey,
  addDownload,
  downloadsComplete,
}: {
  serverHash: string;
  cacheName: string;
  dirPath: string;
  localStorageKey: LocalStorageKey;
  addDownload: DownloadFunction;
  downloadsComplete: DownloadsCompleteFunction;
}) {
  const localHash = localStorageGet(localStorageKey);
  if (localHash === serverHash) {
    return;
  }
  console.log(`refreshing ${cacheName} cache ${localHash} -> ${serverHash}`);
  const manifestUrl = `${dirPath}/${musicManifestFilename}`;
  await addDownload(manifestUrl, {
    action: DownloadQueueAction.Refresh,
    manifestEntry: { hash: serverHash, isManifest: true },
  });
  const { pending } = await downloadsComplete();
  if (pending > 0) {
    // The drain resolved with work left over: entries that failed and backed off, or a drain
    // another tab owns. Recording the hash would leave this client believing it is current
    // forever, so leave it alone - the next walk re-queues the manifest refresh, which is
    // idempotent (issue #3517 item B4).
    console.log(`Not recording ${cacheName} hash ${serverHash}: ${pending} download(s) still queued`);
    return;
  }
  localStorageSet(localStorageKey, serverHash);
  console.log(`Finished refreshing ${cacheName} cache ${localHash} -> ${serverHash}`);
}

/********************************** favorites **********************************/

export async function downloadFavorites(addDownload: DownloadFunction) {
  const favorites = [...localStorageGet(LocalStorageKey.Favorites), ...localStorageGet(LocalStorageKey.FavoritesAdded)];
  const favoritesHash = computeAggregateHash(
    favorites.map(({ hymnalName, songNumber }) => ({
      hash: hymnalName + songNumber.toString(),
    })),
  );
  if (favoritesHash === localStorageGet(LocalStorageKey.FavoritesHash)) {
    return;
  }
  let isAwaitingHymnal = false;
  for (const favorite of favorites) {
    // favorites are resolved through the hymnals the app already has, instead of a manifest fetch
    // per favorite (issue #3517 item C6)
    const hymnal = store.hymnals.find(({ name }) => name === favorite.hymnalName);
    if (!hymnal) {
      // the hymnals have not arrived yet (or this household lost access) - retry on a later walk
      console.log(`Waiting for hymnal ${favorite.hymnalName} to download favorite ${favorite.songNumber}`);
      isAwaitingHymnal = true;
      continue;
    }
    const hymn = hymnal.hymns.find(({ songNumber }) => songNumber === favorite.songNumber.toString());
    if (!hymn) {
      // The hymnal is here and simply has no such song (a removed song, or a stale favorites list).
      // That never resolves, so withholding the hash for it would withhold it for good and
      // re-walk every favorite on every check (issue #3517).
      console.log(`Ignoring favorite ${favorite.hymnalName} ${favorite.songNumber}: not in the hymnal`);
      continue;
    }
    // one helper for every music url prefix, so encoding cannot diverge (issue #3517)
    const urlPrefix = getUrlPrefixForSong({
      hymnalName: favorite.hymnalName,
      songNumber: favorite.songNumber.toString(),
    });
    await addDownload(urlPrefix, {
      action: DownloadQueueAction.Download,
      manifestEntry: { ...getManifestEntryFromHymn(hymn), hash: favoritesHash },
    });
  }
  if (isAwaitingHymnal) {
    // a favorite's hymnal is missing - leave the hash alone so the next check retries
    return;
  }
  localStorageSet(LocalStorageKey.FavoritesHash, favoritesHash);
}
